import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ERROR_MESSAGES = {
  not_allowed:     'Your email is not authorised to use this app. Contact your administrator.',
  oauth_cancelled: 'Sign-in was cancelled.',
  no_email:        'Could not retrieve email from Google. Please try again.',
};

export default function Login() {
  const [params] = useSearchParams();
  const error    = params.get('error');
  const email    = params.get('email');

  const handleSignIn = () => {
    window.location.href = `${BACKEND}/api/gmail/connect`;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6"
      data-testid="login-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-10">
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center mb-5 shadow-md">
            <ShieldCheck className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Supply Chain Auditor
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            Sign in with your Mitsumi Gmail account to access your audits.
          </p>

          {error && (
            <div
              data-testid="login-error"
              className="w-full mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 text-left"
            >
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {ERROR_MESSAGES[error] || `Sign-in failed: ${error}`}
                </p>
                {email && (
                  <p className="text-xs text-red-700 mt-1 font-mono">{email}</p>
                )}
              </div>
            </div>
          )}

          <Button
            data-testid="google-signin-button"
            onClick={handleSignIn}
            size="lg"
            className="w-full gap-2 h-11"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Sign in with Google
          </Button>

          <p className="text-xs text-slate-400 mt-6">
            Only authorised Mitsumi accounts can sign in.
            <br />Your Gmail is used to read inventory emails for auditing.
          </p>
        </div>
      </div>
    </div>
  );
}
